import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isApiError, isOutcomeUnknown, refusalMessage } from '../api';
import { earningsApi } from '../api/earnings';
import { clearDriverSession } from '../lib/driverSession';
import './driverEarningsWalletProfile.css';
import './driverWalletPremium.css';

function moneyOf(minor, currency) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((Number(minor) || 0) / 100);
}

/** "$" for USD: the field's prefix. */
function symbolOf(currency) {
  const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).formatToParts(0);

  return parts.find((part) => part.type === 'currency')?.value ?? currency;
}

/** "12.5" → 1250; anything else, or nothing, → null. The server checks it against what is owed. */
export function minorOf(text) {
  const amount = /^\s*(\d{1,7})(?:[.,](\d{1,2}))?\s*$/.exec(String(text ?? ''));
  if (!amount) return null;
  const minor = Number(amount[1]) * 100 + Number((amount[2] ?? '0').padEnd(2, '0'));

  return minor > 0 ? minor : null;
}

/** "2 Oct": the operator's day, the date part of the moment the server wrote in its zone. */
function dayOf(iso) {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));

  return day
    ? new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]))).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    : '';
}

function IcInfo() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path d="M12 10.2V16M12 7.2v.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IcTxEmpty() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" aria-hidden style={{ margin: '0 auto 0.65rem', display: 'block', color: '#9ca3af' }}>
      <rect x="4" y="6" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ArCashOut() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path d="M12 4v8m0 0-3-3m3 3 3-3" stroke="#b45309" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="5" y="14" width="14" height="6" rx="1.2" stroke="#b45309" strokeWidth="1.4" />
    </svg>
  );
}

/**
 * The rider's wallet as Rio set it (2 Oct 2026; InGo ADR 0030): a statement.
 * Every InGo delivery is cash, so the rider holds the whole fare and owes
 * InGo its commission; this shows the cash collected and the commission on
 * it, since the first delivery, and each delivery's line, from the commission
 * lines recorded at completion (`rider/me/wallet`). No top-ups and no
 * withdrawals: nothing moves through the app.
 *
 * Paying the office (InGo ADR 0033; Rio: "Rider reports, office confirms"):
 * the rider says what they paid, in the design's own amount card; it waits
 * until the office confirms it against the cash it received, and only then
 * comes off what is owed. One waits at a time.
 */
export default function DriverWalletPage({ earnings = earningsApi }) {
  const navigate = useNavigate();
  const [statement, setStatement] = useState(null);
  const [lines, setLines] = useState([]);
  const [next, setNext] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [more, setMore] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [sent, setSent] = useState('');

  const refused = useCallback(
    (err) => {
      if (isApiError(err) && err.status === 401) {
        clearDriverSession();
        navigate('/driver/login', { replace: true });
        return;
      }
      setLoadErr(refusalMessage(err));
    },
    [navigate],
  );

  useEffect(() => {
    let cancelled = false;
    earnings.wallet().then(
      (answer) => {
        if (cancelled) return;
        setStatement(answer);
        setLines(answer.lines ?? []);
        setNext(answer.next);
      },
      (err) => !cancelled && refused(err),
    );

    return () => {
      cancelled = true;
    };
  }, [earnings, refused]);

  /** The statement again, after a payment is sent: what waits now. */
  const reload = async () => {
    const answer = await earnings.wallet();
    setStatement(answer);
    setLines(answer.lines ?? []);
    setNext(answer.next);

    return answer;
  };

  const sendPayment = async (event) => {
    event.preventDefault();
    const minor = minorOf(amount);
    setProblem('');
    setSent('');
    if (minor === null) {
      setProblem('Say the amount, like 12.50.');
      return;
    }
    setBusy(true);
    try {
      const { message } = await earnings.remit(minor, note);
      setAmount('');
      setNote('');
      setSent(message || 'Sent.');
      await reload().catch(() => {});
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        refused(err);
        return;
      }
      // The connection dropped after sending: the statement says whether it arrived.
      const arrived = isOutcomeUnknown(err) ? await reload().then((answer) => Boolean(answer.waiting), () => false) : false;
      if (arrived) {
        setAmount('');
        setNote('');
      } else {
        setProblem(refusalMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const showMore = async () => {
    setMore(true);
    try {
      const page = await earnings.wallet(next);
      setLines((shown) => [...shown, ...(page.lines ?? [])]);
      setNext(page.next);
    } catch (err) {
      refused(err);
    } finally {
      setMore(false);
    }
  };

  const currency = statement?.currency || 'USD';
  const owed = statement ? (statement.owed_minor ?? statement.commission_owed_minor) : null;
  const paid = statement?.paid_minor ?? 0;
  const waiting = statement?.waiting ?? null;
  const answered = (statement?.remittances ?? []).filter((remittance) => remittance.status !== 'pending');

  return (
    <div className="dvRoot dvRoot--wallet-premium" role="main">
      <header className="dvH">
        <span className="dv__heroSpacer" aria-hidden />
        <h1>My Wallet</h1>
        <span className="dv__heroSpacer" aria-hidden />
      </header>
      <div className="dvSc">
        {loadErr ? (
          <p className="dw-alert dw-alert--error" role="alert">
            {loadErr}
          </p>
        ) : null}

        <section className="dw-walletHero" aria-label="Commission owed">
          <p className="dwbL1">Commission owed to InGo</p>
          <p className="dwbMain">{statement ? moneyOf(owed, currency) : '…'}</p>
          {statement ? (
            <p className="dwbCodNote" role="note">
              From {moneyOf(statement.cash_collected_minor, currency)} you collected in cash on {statement.deliveries}{' '}
              {statement.deliveries === 1 ? 'delivery' : 'deliveries'}. You kept {moneyOf(statement.kept_minor, currency)}.
              {paid > 0 ? ` The office confirmed ${moneyOf(paid, currency)} of ${moneyOf(statement.commission_owed_minor, currency)}.` : ''}
            </p>
          ) : null}
        </section>

        {sent ? (
          <p className="dw-alert dw-alert--success" role="status">
            {sent}
          </p>
        ) : null}

        {waiting ? (
          <section className="dw-withdrawCard" aria-label="Waiting for the office">
            <h2 className="dwwT">Waiting for the office</h2>
            <p className="dvTxx">
              {moneyOf(waiting.amount_minor, waiting.currency)} · sent {dayOf(waiting.created_at)}
            </p>
            {waiting.note ? <p className="dw-withdrawNote">{waiting.note}</p> : null}
          </section>
        ) : statement && owed > 0 ? (
          <form className="dw-withdrawCard" aria-label="I've paid the office" onSubmit={sendPayment} noValidate>
            <h2 className="dwwT">I've paid the office</h2>
            <div className="dw-withdrawTop">
              <button type="button" className="dw-withdrawAll" onClick={() => setAmount((owed / 100).toFixed(2))}>
                All {moneyOf(owed, currency)}
              </button>
            </div>
            <div className="dw-withdrawField">
              <span className="dw-withdrawPrefix" aria-hidden>
                {symbolOf(currency)}
              </span>
              <input
                className="dw-withdrawInput"
                inputMode="decimal"
                aria-label="Amount you paid"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
            <div className="dw-withdrawField">
              <input
                className="dw-withdrawInput"
                aria-label="Note for the office"
                placeholder="How you paid (optional)"
                maxLength={200}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
            {problem ? (
              <p className="dw-alert dw-alert--error" role="alert">
                {problem}
              </p>
            ) : null}
            <button type="submit" className="dw-withdrawBtn" disabled={busy}>
              {busy ? 'Sending…' : 'Send to the office'}
            </button>
            <p className="dw-withdrawNote">It comes off when the office confirms it.</p>
          </form>
        ) : null}

        <div className="dw-infoCard" role="note">
          <span className="dw-infoCardIcon" aria-hidden>
            <IcInfo />
          </span>
          <p className="dw-infoCardText">
            Clients pay you in cash. InGo's commission on each delivery is recorded when you complete it. Pay it at the office, then
            say so here.
          </p>
        </div>

        {answered.length > 0 ? (
          <>
            <h2 className="dvRsec">Payments to the office</h2>
            <div className="dw-txList">
              {answered.map((remittance) => (
                <div className="dvTxR" key={`remittance-${remittance.id}`}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="dvTxx">
                      {remittance.status_label} · sent {dayOf(remittance.created_at)}
                    </div>
                    {remittance.status === 'declined' && remittance.decline_reason ? (
                      <span className="dvTdt">{remittance.decline_reason}</span>
                    ) : null}
                  </div>
                  <div className="dvTamt">{moneyOf(remittance.amount_minor, remittance.currency)}</div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        <h2 className="dvRsec">Deliveries</h2>
        {statement && lines.length === 0 ? (
          <div className="dw-txEmpty" role="status">
            <IcTxEmpty />
            <p>No cash deliveries yet.</p>
          </div>
        ) : null}
        {lines.length > 0 ? (
          <div className="dw-txList">
            {lines.map((line) => (
              <div className="dvTxR" key={line.id}>
                <div className="dvAr" aria-hidden>
                  <ArCashOut />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="dvTxx">
                    #{line.reference} · {line.pickup} → {line.dropoff}
                  </div>
                  <span className="dvTdt">
                    {new Date(line.completed_at).toLocaleString()} · cash {moneyOf(line.fare_minor, line.currency)}
                  </span>
                </div>
                <div className="dvTamt dvTamtC">{moneyOf(line.commission_minor, line.currency)}</div>
              </div>
            ))}
          </div>
        ) : null}
        {next ? (
          <button type="button" className="dvPil" style={{ margin: '0.75rem auto', display: 'block' }} disabled={more} onClick={showMore}>
            {more ? 'Loading…' : 'Show older deliveries'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
